import {Metadata, RemoteConstantsModel} from "../models/remoteConstants.model";
import {SectionKey} from "./branchDiff";

// Sections currently supported by the whole-record migration flow. Metadata
// sections all share the same record shape (uid+id+values+entities+requires)
// + the same satellite data (localization rows, image pack, image rows), so
// they go through one classifier and one command.
const METADATA_SECTION_KEYS: SectionKey[] = [
    'unitAttributes', 'buildingAttributes',
    'techActionAttributes', 'techPassiveAttributes',
    'rewards',
];

export function isMetadataSection(sectionKey: SectionKey): boolean {
    return METADATA_SECTION_KEYS.includes(sectionKey);
}

export interface MetadataMigration {
    sectionKey: SectionKey;
    record: Metadata;
    // localization[lang][key] entries to copy from Other into Current. Only
    // entries actually present in Other are included.
    locRows: { lang: string; key: string; value: string }[];
    // The pack row at imagePacks[record.uid], if present in Other.
    packRow?: string[][];
    // images[key] entries that need to come over (not already in Current).
    imageRows: { key: string; value: unknown }[];
}

export interface MigrationClassification {
    resolvable: boolean;
    migration?: MetadataMigration;
    // Human-readable list of records the user has to bring over (or
    // create) on Current first. Suitable for a tooltip.
    missingDeps?: string[];
}

function getMetaCollection(state: RemoteConstantsModel, sectionKey: SectionKey): Metadata[] {
    switch (sectionKey) {
        case 'unitAttributes':        return state.unitAttributes ?? [];
        case 'buildingAttributes':    return state.buildingAttributes ?? [];
        case 'techActionAttributes':  return state.techActionAttributes ?? [];
        case 'techPassiveAttributes': return state.techPassiveAttributes ?? [];
        case 'rewards':               return state.rewards ?? [];
        default: return [];
    }
}

function collectEntityIds(state: RemoteConstantsModel): Set<string> {
    const s = new Set<string>();
    state.units?.forEach(u => s.add(u.id));
    state.buildings?.forEach(b => s.add(b.id));
    state.tiles?.forEach(t => s.add(t.id));
    state.resources?.forEach(r => s.add(r.id));
    state.techTree?.forEach(n => s.add(n.id));
    return s;
}

export function classifyMetadataMigration(
    targetRecord: Metadata,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
    currentBranchLabel: string,
): MigrationClassification {
    if (!isMetadataSection(sectionKey)) {
        return {resolvable: false, missingDeps: [`Section ${sectionKey} migration not yet supported.`]};
    }

    const missingDeps: string[] = [];
    const currentMeta = getMetaCollection(currentState, sectionKey);
    const currentEntityIds = collectEntityIds(currentState);

    // Uid collision: extremely unlikely for uuids but a clear bug if it
    // happens, so check up-front.
    if (currentMeta.some(m => m.uid === targetRecord.uid)) {
        return {
            resolvable: false,
            missingDeps: [`A record with the same uid (${targetRecord.uid}) already exists on current (${currentBranchLabel})`],
        };
    }

    // `requires`: each entry is an attribute id (same-section). Current
    // must have at least one record with that id in the same collection
    // for the requirement to be satisfiable post-migration.
    if (targetRecord.requires && targetRecord.requires.length > 0) {
        const currentIds = new Set(currentMeta.map(m => m.id));
        for (const req of targetRecord.requires) {
            if (!currentIds.has(req)) {
                missingDeps.push(`Required attribute "${req}" not present in Current.`);
            }
        }
    }

    // `entities.<slot> = entityUid` — each referenced entity must exist on
    // Current. We accept any of units/buildings/tiles/resources/techTree
    // (the slot semantics depend on the attribute's runtime code).
    for (const [slot, entityUid] of Object.entries(targetRecord.entities ?? {})) {
        if (!entityUid) continue;
        if (!currentEntityIds.has(entityUid)) {
            missingDeps.push(`Entity for slot "${slot}" (${resolveName(entityUid)}) not present in Current.`);
        }
    }

    // `values.<entityUid> = N` — EntityValue params use an entity uid as
    // the key. Distinguish heuristically: if a values-key looks like a uid
    // we recognise (in either branch), check it. Otherwise it's a named
    // parameter (e.g. "attackBonus") and no check needed.
    const knownInTarget = collectEntityIds(targetState);
    for (const key of Object.keys(targetRecord.values ?? {})) {
        if (!knownInTarget.has(key)) continue;       // not a uid-typed key
        if (!currentEntityIds.has(key)) {
            missingDeps.push(`Parameter for entity (${resolveName(key)}) not present in Current.`);
        }
    }

    if (missingDeps.length > 0) {
        return {resolvable: false, missingDeps};
    }

    // Build the migration package. Loc, pack and image rows are all
    // additive — if the same key already exists on Current we skip it at
    // command time to avoid overwriting Current's content.
    const locRows: MetadataMigration['locRows'] = [];
    const langs = Object.keys(targetState.localization ?? {});
    const wantedKeys = [targetRecord.uid, `${targetRecord.id}.description`];
    for (const lang of langs) {
        const table = targetState.localization?.[lang] ?? {};
        for (const k of wantedKeys) {
            if (k in table) locRows.push({lang, key: k, value: table[k]});
        }
    }

    const packRow = targetState.imagePacks?.[targetRecord.uid];
    const imageRows: MetadataMigration['imageRows'] = [];
    if (packRow) {
        const referenced = new Set<string>();
        for (const variations of packRow) for (const key of variations) referenced.add(key);
        referenced.forEach(key => {
            if (!targetState.images?.[key]) return;
            // Only include images Current doesn't already have, so we don't
            // overwrite display names etc. designers set in Current.
            if (currentState.images?.[key] !== undefined) return;
            imageRows.push({key, value: targetState.images[key]});
        });
    }

    return {
        resolvable: true,
        migration: {
            sectionKey,
            record: targetRecord,
            locRows,
            packRow,
            imageRows,
        },
    };
}
